import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { api, fetchBlob } from "../api/client";
import type { AuditEvent, CountBoard, EConsentRecord, PassportDetail, PassportSpecimen } from "../api/types";
import { AuthImage } from "../components/AuthImage";
import { AppleWalletMark } from "../components/AppleWalletMark";
import { confirmDeletePassport } from "../components/PassportDrawer";
import { WalletAddQr } from "../components/WalletAddQr";
import { useAuth } from "../auth/AuthContext";
import { ahoSignIn, ahoTimeOut } from "../lib/ahoCopy";
import { formatClock, formatDisplay } from "../lib/dates";
import { econsentFieldOrder, econsentFieldValue, econsentLabel, econsentSlots } from "../lib/econsentCopy";
import { countCategories, countLabel, intraopCopy, isInPatient, lateralityLabel } from "../lib/intraopCopy";
import { lifecycleSteps, statusClass, statusLabel, verificationLabel } from "../lib/status";

function Field({ label, value }: { label: string; value?: string | null }) {
  return (
    <div className="field-block">
      <div className="field-label">{label}</div>
      <div className="field-value">{value || "—"}</div>
    </div>
  );
}

function clinicSigLabel(role: string) {
  if (role === "patient") return "Patient Signature";
  if (role === "surgeon") return "Surgeon Signature";
  if (role === "witness") return "Witness";
  if (role === "circulator") return "Circulator";
  if (role === "cancel") return "Cancel Surgical Passport";
  return role;
}

function ahoStamp(detail: PassportDetail, key: string) {
  return (detail.verifications || []).find((v) => {
    if (!v.matched) return false;
    if (v.type === `aho_${key}`) return true;
    return String(v.payload?.key || "") === key;
  });
}

function countStateLabel(state: string) {
  return isInPatient(state) ? intraopCopy.inPatient : intraopCopy.out;
}

export function PassportRecordPage() {
  const { passportId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [detail, setDetail] = useState<PassportDetail | null>(null);
  const [audit, setAudit] = useState<AuditEvent[]>([]);
  const [specimens, setSpecimens] = useState<PassportSpecimen[]>([]);
  const [countBoard, setCountBoard] = useState<CountBoard | null>(null);
  const [econsent, setEconsent] = useState<EConsentRecord | null>(null);
  const [error, setError] = useState("");
  const [printBusy, setPrintBusy] = useState("");
  const [deleteBusy, setDeleteBusy] = useState(false);

  const load = useCallback(async () => {
    if (!passportId) return;
    const data = await api<PassportDetail>(`/api/portal/passports/${passportId}`);
    setDetail(data);
    const trail = await api<AuditEvent[]>(`/api/passports/${passportId}/audit`);
    setAudit(trail);
    const rows = await api<PassportSpecimen[]>(`/api/passports/${passportId}/specimens`);
    setSpecimens(rows);
    try {
      setCountBoard(await api<CountBoard>(`/api/passports/${passportId}/count-board`));
    } catch {
      setCountBoard(null);
    }
    try {
      setEconsent(await api<EConsentRecord>(`/api/passports/${passportId}/econsent`));
    } catch {
      setEconsent(null);
    }
    setError("");
    return data;
  }, [passportId]);

  useEffect(() => {
    (async () => {
      try {
        await load();
      } catch (e) {
        setError(e instanceof Error ? e.message : String(e));
      }
    })();
  }, [load]);

  async function printDoc(kind: "consent" | "report" | "record") {
    if (!detail) return;
    setPrintBusy(kind);
    try {
      const path =
        kind === "consent"
          ? `/api/passports/${detail.id}/consent/pdf`
          : kind === "report"
            ? `/api/passports/${detail.id}/report/pdf`
            : `/api/passports/${detail.id}/record/pdf`;
      const meta = await api<{ download_url: string }>(path, { method: "POST" });
      const blob = await fetchBlob(meta.download_url);
      const url = URL.createObjectURL(blob);
      window.open(url, "_blank", "noopener,noreferrer");
    } finally {
      setPrintBusy("");
    }
  }

  async function removePassport() {
    if (!detail) return;
    setDeleteBusy(true);
    try {
      if (await confirmDeletePassport(detail.passport_code, detail.id)) {
        navigate("/main");
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setDeleteBusy(false);
    }
  }

  if (!detail) {
    return error ? <div className="banner-error">{error}</div> : <div className="muted">Loading Surgical Passport…</div>;
  }

  const canPrint = Boolean(detail.locked_at) || detail.status !== "draft";
  const steps = lifecycleSteps(detail);
  const tone = statusClass(detail.status);
  const clinicSigs = [...(detail.signatures || [])].sort((a, b) => {
    const order = ["patient", "surgeon", "witness", "circulator", "cancel"];
    return order.indexOf(a.role) - order.indexOf(b.role);
  });
  const otherVerifications = (detail.verifications || []).filter((v) => !v.type.startsWith("aho_"));
  const hasAho =
    ahoSignIn.some((item) => ahoStamp(detail, item.key)) || ahoTimeOut.some((item) => ahoStamp(detail, item.key));
  const econsentLang = econsent?.language || "en";

  return (
    <div className="record-page">
      <div className="record-head">
        <div>
          <Link to="/main" className="muted">
            Main
          </Link>
          <h2>
            {detail.passport_code}
            <AppleWalletMark show={detail.in_apple_wallet} />
          </h2>
          <div className="muted">{detail.patient_name}</div>
          <span className={`status-pill ${tone}`}>{statusLabel(detail)}</span>
        </div>
        <div className="print-row">
          <button type="button" disabled={!canPrint || printBusy === "consent"} onClick={() => void printDoc("consent")}>
            {printBusy === "consent" ? "Preparing…" : "Print consent"}
          </button>
          <button type="button" disabled={!canPrint || printBusy === "report"} onClick={() => void printDoc("report")}>
            {printBusy === "report" ? "Preparing…" : "Print report"}
          </button>
          <button type="button" disabled={!canPrint || printBusy === "record"} onClick={() => void printDoc("record")}>
            {printBusy === "record" ? "Preparing…" : "Print record"}
          </button>
          {isAdmin && (
            <button type="button" className="danger" disabled={deleteBusy} onClick={() => void removePassport()}>
              {deleteBusy ? "Working…" : "Delete"}
            </button>
          )}
        </div>
      </div>
      {error && <div className="banner-error">{error}</div>}

      <section className="record-card">
        <h4 className="field-label">Patient</h4>
        <div className="record-grid">
          <Field label="Patient" value={detail.patient_name} />
          {detail.patient_middle_name ? <Field label="Middle name" value={detail.patient_middle_name} /> : null}
          <Field label="MRN" value={detail.patient_mrn || "MRN pending"} />
          <Field label="DOB" value={formatDisplay(detail.patient_dob, false)} />
        </div>
        {detail.has_patient_face ? (
          <AuthImage className="capture-img" path={`/api/patients/${detail.patient_id}/face`} alt="Face ID" />
        ) : null}
      </section>

      <section className="record-card">
        <h4 className="field-label">Procedure</h4>
        <div className="record-grid">
          <Field label="Procedure" value={detail.procedure} />
          <Field label="Side / Laterality" value={detail.laterality} />
          <Field label={intraopCopy.site} value={detail.anatomical_site} />
          <Field label="Robotic" value={detail.robotic ? "Yes" : "No"} />
          {detail.body_view ? <Field label="Body view" value={detail.body_view} /> : null}
          <Field label="Surgeon" value={detail.surgeon_name} />
          <Field label="Initial Encounter" value={formatDisplay(detail.encounter_at)} />
          {detail.notes ? <Field label="Notes" value={detail.notes} /> : null}
        </div>
      </section>

      {detail.site_marking_path ? (
        <section className="record-card">
          <h4 className="field-label">Side / Site Marking</h4>
          <AuthImage className="site-mark" path={`/api/passports/${detail.id}/site-marking`} alt="Side / Site Marking" />
        </section>
      ) : null}

      {detail.status !== "draft" ? (
        <section className="record-card">
          <WalletAddQr passportId={detail.id} show />
        </section>
      ) : null}

      <section className="record-card">
        <h4 className="field-label">Schedule / suite</h4>
        <div className="record-grid">
          <Field label="Scheduled OR time" value={formatDisplay(detail.scheduled_or_at)} />
          <Field label="Time to arrive" value={formatClock(detail.facility_at)} />
          <Field label="Facility" value={detail.facility} />
          <Field label="OR suite" value={detail.or_suite} />
        </div>
      </section>

      {econsent ? (
        <section className="record-card">
          <h4 className="field-label">E-Consent</h4>
          <div className="muted">{econsent.form_code}</div>
          <div className="record-grid">
            <Field label="Date" value={formatDisplay(econsent.stamped_at, false)} />
            <Field label="Time" value={formatClock(econsent.stamped_at)} />
          </div>
          <div className="record-grid">
            {econsentFieldOrder.map((key) => {
              const value = econsentFieldValue(econsentLang, key, econsent.fields?.[key]);
              if (!value) return null;
              return <Field key={key} label={econsentLabel(econsentLang, key)} value={value} />;
            })}
          </div>
          {econsentSlots.map((slot) => {
            const row = (econsent.signatures || []).find((s) => s.slot === slot);
            if (!row || (!row.image_path && !row.printed_name)) return null;
            return (
              <div className="sig" key={slot}>
                <div>
                  <strong>{econsentLabel(econsentLang, slot)}</strong>
                  {row.printed_name ? ` · ${row.printed_name}` : ""}
                </div>
                {row.signed_at ? <div className="muted">{formatDisplay(row.signed_at)}</div> : null}
                {row.image_path ? (
                  <AuthImage
                    className="sig-pad"
                    path={`/api/passports/${detail.id}/econsent/signatures/${slot}`}
                    alt={econsentLabel(econsentLang, slot)}
                  />
                ) : null}
              </div>
            );
          })}
        </section>
      ) : null}

      <section className="record-card">
        <h4 className="field-label">{intraopCopy.specimenSectionTitle}</h4>
        {specimens.length === 0 && <div className="empty">—</div>}
        {specimens.map((row) => (
          <div className="sig" key={row.id}>
            <div>
              <strong>{row.name || intraopCopy.specimenSectionTitle}</strong>
              {row.site ? ` · ${row.site}` : ""}
              {" · "}
              {lateralityLabel[row.laterality] || row.laterality}
            </div>
            {row.actor_name && <div className="muted">{row.actor_name}</div>}
            <div className="muted">{formatDisplay(row.signed_at || row.created_at)}</div>
            {row.photo_path ? (
              <AuthImage
                className="capture-img"
                path={`/api/passports/${detail.id}/specimens/${row.id}/photo`}
                alt={intraopCopy.specimenSectionTitle}
              />
            ) : null}
            {row.signature_path ? (
              <AuthImage
                className="sig-pad"
                path={`/api/passports/${detail.id}/specimens/${row.id}/signature`}
                alt="Surgeon Signature"
              />
            ) : null}
          </div>
        ))}
      </section>

      {countBoard && (
        <section className="record-card">
          <h4 className="field-label">{intraopCopy.phaseTitle}</h4>
          {countCategories.map((kind) => {
            const category = countBoard.categories.find((row) => row.category === kind.wire);
            const items = category?.items || [];
            const outCount = items.filter((item) => !isInPatient(item.state)).length;
            const inBody = items.filter((item) => isInPatient(item.state)).length;
            return (
              <div className="count-category" key={kind.wire}>
                <div className="count-category-title">{kind.title}</div>
                <div className="record-grid">
                  <Field label={countLabel(kind.title)} value={String(category?.count_before_incision ?? 0)} />
                  <Field label={intraopCopy.out} value={String(outCount)} />
                  <Field label={intraopCopy.currentlyInBody} value={String(inBody)} />
                </div>
                {items.length > 0 ? (
                  <div className="count-tiles">
                    {items.map((item) => (
                      <span className={`count-tile ${isInPatient(item.state) ? "in" : "out"}`} key={item.id}>
                        {item.ordinal} · {countStateLabel(item.state)}
                      </span>
                    ))}
                  </div>
                ) : null}
              </div>
            );
          })}
        </section>
      )}

      {detail.status === "cancelled" && (
        <section className="record-card">
          <h4 className="field-label">Cancel Surgical Passport</h4>
          <div className="field-value">{formatDisplay(detail.cancelled_at)}</div>
          {detail.cancel_reason ? <div className="muted">{detail.cancel_reason}</div> : null}
        </section>
      )}

      <section className="record-card">
        <h4 className="field-label">Status</h4>
        <ol className="lifecycle">
          {steps.map((step) => (
            <li key={step.id} className={step.state}>
              <div>
                <div className="life-title">{step.title}</div>
                <div className="life-time">{step.at ? formatDisplay(step.at) : "—"}</div>
              </div>
            </li>
          ))}
        </ol>
      </section>

      {hasAho ? (
        <section className="record-card">
          <h4 className="field-label">Room Sign In</h4>
          {ahoSignIn.map((item) => {
            const stamp = ahoStamp(detail, item.key);
            return (
              <div className="sig" key={item.key}>
                <div>{item.title}</div>
                <div className="muted">
                  {stamp ? formatDisplay(stamp.created_at) : "—"}
                  {stamp?.actor_name ? ` · ${stamp.actor_name}` : ""}
                </div>
              </div>
            );
          })}
          <h4 className="field-label">Time Out</h4>
          {ahoTimeOut.map((item) => {
            const stamp = ahoStamp(detail, item.key);
            return (
              <div className="sig" key={item.key}>
                <div>{item.title}</div>
                <div className="muted">
                  {stamp ? formatDisplay(stamp.created_at) : "—"}
                  {stamp?.actor_name ? ` · ${stamp.actor_name}` : ""}
                </div>
              </div>
            );
          })}
        </section>
      ) : null}

      <section className="record-card">
        <h4 className="field-label">Signatures</h4>
        {clinicSigs.length === 0 && <div className="empty">No signatures yet.</div>}
        {clinicSigs.map((s, i) => (
          <div className="sig" key={`${s.role}-${i}`}>
            <div>
              <strong>{clinicSigLabel(s.role)}</strong> · {s.printed_name}
            </div>
            <div className="muted">{formatDisplay(s.signed_at)}</div>
            {s.image_path ? (
              <AuthImage className="sig-pad" path={`/api/passports/${detail.id}/signatures/${s.role}`} alt={clinicSigLabel(s.role)} />
            ) : null}
          </div>
        ))}
      </section>

      {otherVerifications.length > 0 && (
        <section className="record-card">
          {otherVerifications.map((v) => (
            <div className="sig" key={v.id}>
              <div>
                <strong>{verificationLabel(v.type)}</strong>
              </div>
              {v.actor_name && <div className="muted">{v.actor_name}</div>}
              <div className="muted">{formatDisplay(v.created_at)}</div>
            </div>
          ))}
        </section>
      )}

      <section className="record-card">
        <h4 className="field-label">Audit</h4>
        {audit.length === 0 && <div className="empty">—</div>}
        {audit.map((e) => (
          <div className="sig" key={e.id}>
            <div>
              <strong>{e.action}</strong>
            </div>
            {e.actor_name && <div className="muted">{e.actor_name}</div>}
            <div className="muted">{formatDisplay(e.created_at)}</div>
          </div>
        ))}
      </section>
    </div>
  );
}
